'use client'

import * as React from 'react'
import { cn } from '@/lib/utils'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useActiveAgents } from '@/lib/hooks'
import type { Agent } from '@/lib/api/types'
import { Bot, Loader2 } from 'lucide-react'

export interface AgentSelectorProps {
  value?: string
  onChange: (agentSlug: string) => void
  disabled?: boolean
  className?: string
}

/**
 * Dropdown selector for AI agents
 * Fetches available agents and displays them with avatars
 */
export function AgentSelector({
  value,
  onChange,
  disabled = false,
  className,
}: AgentSelectorProps) {
  const { data: agents = [], isLoading, error } = useActiveAgents()

  // Find selected agent for display
  const selectedAgent = React.useMemo(
    () => agents.find((a) => a.slug === value),
    [agents, value]
  )

  if (error) {
    return (
      <div className={cn('text-sm text-red-500', className)}>
        Failed to load agents
      </div>
    )
  }

  return (
    <Select
      value={value}
      onValueChange={onChange}
      disabled={disabled || isLoading}
    >
      <SelectTrigger className={cn('w-[280px]', className)}>
        {isLoading ? (
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-gray-500">Loading agents...</span>
          </div>
        ) : selectedAgent ? (
          <AgentOption agent={selectedAgent} />
        ) : (
          <SelectValue placeholder="Select an agent" />
        )}
      </SelectTrigger>
      <SelectContent>
        {agents.map((agent) => (
          <SelectItem
            key={agent.id}
            value={agent.slug}
            className="cursor-pointer"
          >
            <AgentOption agent={agent} showDescription />
          </SelectItem>
        ))}
        {agents.length === 0 && !isLoading && (
          <div className="px-2 py-4 text-center text-sm text-gray-500">
            No agents available
          </div>
        )}
      </SelectContent>
    </Select>
  )
}

/**
 * Renders an agent option with avatar, name, and optional description
 */
function AgentOption({
  agent,
  showDescription = false,
}: {
  agent: Agent
  showDescription?: boolean
}) {
  return (
    <div className="flex items-center gap-3">
      <Avatar className="h-6 w-6">
        {agent.avatarUrl && (
          <AvatarImage src={agent.avatarUrl} alt={agent.name} />
        )}
        <AvatarFallback className="bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300">
          <Bot className="h-3 w-3" />
        </AvatarFallback>
      </Avatar>
      <div className="flex flex-col">
        <span className="text-sm font-medium">{agent.name}</span>
        {showDescription && agent.description && (
          <span className="line-clamp-1 text-xs text-gray-500">
            {agent.description}
          </span>
        )}
      </div>
    </div>
  )
}
